// =============================================================================
// TabGeneral.jsx
// =============================================================================
// Top-level request fields — the first (non-child) step of the wizard / form.
//
// Renders: contact name display, shelter client ref, contact phone, delivery
// address (via DeliverySelect), notes for Zephyr, and CC-colleagues checkbox
// list. This is the only step that is NOT per-child.
//
// Notes: the delivery value selected here flows down to the laptop step as a
// default (see generalDelivery prop on ChildLaptopSection inside ChildTab).
//
// LEGACY SCHEMA ONLY — do not add new features here.
//
// Pulled out of RequestWizard.jsx as part of the wizard decomposition
// (May 2026).
//
// Props
// ─────
//   d                — general fields object { clientRef, phone, delivery, notes }
//   set              — (field, value) => void  setter for d fields
//   deliveryAddresses — registered shelter delivery options for DeliverySelect
//   locked           — boolean; when true renders read-only LockedField rows
//   user             — current user object (name display + HelpButton)
//   shelterUsers     — array of co-workers eligible to CC
//   ccEmails         — currently ticked CC email addresses
//   setCcEmails      — setter for ccEmails (omit to hide the CC panel)
// =============================================================================
import React from "react";
import { isValidAUPhone } from "../../utils/formConstants";
import { TxtInput } from "../../utils/ui.jsx";
import { HelpButton } from "./ChildSections.jsx";
import { Grid2, LockedBanner, LockedField } from "./FormUI.jsx";
import { ShelterDeliverySelect } from "./ShelterDeliverySelect.jsx";

export function TabGeneral({
  d,
  set,
  deliveryAddresses,
  locked,
  user,
  shelterUsers,
  ccEmails,
  setCcEmails,
}) {
  const [phoneErr, setPhoneErr] = React.useState("");

  function onPhone(v) {
    set("phone", v);
    if (!v) {
      setPhoneErr("");
      return;
    }
    setPhoneErr(
      isValidAUPhone(v) ? "" : "Enter a valid Australian mobile (04xx) or landline (02/03/07/08)"
    );
  }

  if (locked)
    return (
      <div>
        <LockedBanner />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <LockedField label="Client Ref" value={d.clientRef} />
          <LockedField label="Mobile" value={d.mobile} />
          <LockedField label="Phone" value={d.phone} />
          <LockedField label="Delivery Address" value={d.delivery} wide />
          <LockedField label="Notes" value={d.notes} wide />
        </div>
      </div>
    );
  return (
    <div>
      <p style={{ color: "#607D79", fontSize: 13, marginBottom: 14 }}>
        Enter general information about this request.
      </p>
      {user && (user.firstName || user.lastName || user.shelterName) && (
        <div
          style={{
            marginBottom: 14,
            background: "#F5F7F7",
            border: "1px solid #CFE0DE",
            borderRadius: 7,
            padding: "9px 13px",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "#607D79", marginBottom: 3 }}>
            CONTACT NAME
          </div>
          <div style={{ fontSize: 14, color: "#1A2E2B" }}>
            {user.firstName || user.lastName
              ? [user.firstName, user.lastName].filter(Boolean).join(" ")
              : user.shelterName}
          </div>
          <div style={{ fontSize: 11, color: "#90A4A0", marginTop: 2 }}>
            {user.firstName || user.lastName
              ? "Logged-in user — update via your profile if incorrect"
              : "Showing DFV Service name — ask your Zephyr admin to add your personal name to your profile"}
          </div>
        </div>
      )}
      <Grid2>
        <TxtInput
          label="Client Reference (min 5 - max 25 characters — your Organisation's Reference for your Client)"
          value={d.clientRef}
          onChange={(v) => set("clientRef", v.slice(0, 25))}
          placeholder="Your internal ref"
        />
        {/* Spacer so the two contact numbers share the next row (Mobile left, Phone
            right). Mobile first — there are more mobiles than landlines (John). */}
        <div />
        <TxtInput
          label={
            <>
              Contact Mobile <HelpButton helpKey="general_contact_phone" user={user} />
            </>
          }
          value={d.mobile}
          onChange={(v) => set("mobile", v)}
          placeholder="e.g. 0412 345 678"
        />
        <TxtInput
          label="Contact Phone"
          value={d.phone}
          onChange={onPhone}
          error={phoneErr}
          placeholder="e.g. 07 3123 4567"
        />
        <div style={{ gridColumn: "span 2", fontSize: 12, color: "#607D79", marginTop: -4 }}>
          Enter at least one contact number — mobile or landline.
        </div>
        <ShelterDeliverySelect
          value={d.delivery}
          onChange={(v) => set("delivery", v)}
          onPartsChange={(parts) => set("deliveryParts", parts)}
          deliveryAddresses={deliveryAddresses}
          help={<HelpButton helpKey="general_delivery" user={user} />}
        />
        <TxtInput
          label="Notes for Zephyr"
          value={d.notes}
          onChange={(v) => set("notes", v)}
          rows={3}
          placeholder="Add brief context information for Zephyr team and relevant matters to consider"
        />
        {setCcEmails ? (
          <div
            style={{
              background: "#F5FAFA",
              border: `1px solid ${"#CFE0DE"}`,
              borderRadius: 10,
              padding: "12px 16px",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: "#00695C", marginBottom: 6 }}>
              📧 CC Colleagues
            </div>
            <div style={{ fontSize: 12, color: "#607D79", marginBottom: 8 }}>
              Tick colleagues who should also see this request and receive comment notifications:
            </div>
            {shelterUsers && shelterUsers.length > 0 ? (
              shelterUsers.map(function (su) {
                var email = su.user_email;
                var name = su.display_name || email;
                var checked = (ccEmails || []).indexOf(email) !== -1;
                return (
                  <label
                    key={email}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 5,
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={function (e) {
                        if (e.target.checked) {
                          setCcEmails(function (prev) {
                            return prev.concat([email]);
                          });
                        } else {
                          setCcEmails(function (prev) {
                            return prev.filter(function (x) {
                              return x !== email;
                            });
                          });
                        }
                      }}
                      style={{ width: 14, height: 14, accentColor: "#00897B", cursor: "pointer" }}
                    />
                    <span style={{ color: "#1A2E2B" }}>{name}</span>
                    <span style={{ color: "#607D79" }}>({email})</span>
                  </label>
                );
              })
            ) : (
              <div style={{ fontSize: 12, color: "#607D79", fontStyle: "italic" }}>
                No other caseworkers at this DFV Service yet. Ask your Zephyr admin to add
                colleagues so you can CC them on requests.
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
      </Grid2>
    </div>
  );
}
